import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const FeatureFlagsContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

export const FeatureFlagsProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [flags, setFlags] = useState({});
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFlags = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/api/features/flags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlags(response.data.flags || {});
    } catch (error) {
      console.error('Failed to fetch feature flags:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchRoadmap = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/api/features/roadmap`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoadmap(response.data);
    } catch (error) {
      console.error('Failed to fetch roadmap:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Check if a specific feature is enabled for the current user
  const isEnabled = useCallback((flagId) => {
    const flag = flags[flagId];
    if (!flag) return false;
    return flag.enabled && flag.user_has_access;
  }, [flags]);

  // Check if a feature is visible (even if not enabled)
  const isVisible = useCallback((flagId) => {
    const flag = flags[flagId];
    return flag?.visible || false;
  }, [flags]);

  // Check if feature requires upgrade
  const requiresUpgrade = useCallback((flagId) => {
    const flag = flags[flagId];
    if (!flag) return false;
    return flag.requires_upgrade || false;
  }, [flags]);

  // Get the tier required for a feature
  const getTierRequired = useCallback((flagId) => {
    const flag = flags[flagId];
    return flag?.tier_required || 'starter';
  }, [flags]);

  // Get feature status
  const getStatus = useCallback((flagId) => {
    const flag = flags[flagId];
    return flag?.status || 'planned';
  }, [flags]);

  // Get full feature info
  const getFeature = useCallback((flagId) => {
    return flags[flagId] || null;
  }, [flags]);

  // Refresh flags
  const refresh = useCallback(async () => {
    await fetchFlags();
  }, [fetchFlags]);

  return (
    <FeatureFlagsContext.Provider value={{
      flags,
      roadmap,
      loading,
      isEnabled,
      isVisible,
      requiresUpgrade,
      getTierRequired,
      getStatus,
      getFeature,
      fetchRoadmap,
      refresh
    }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  }
  return context;
};

// HOC to conditionally render based on feature flag
export const withFeatureFlag = (WrappedComponent, flagId, FallbackComponent = null) => {
  return function WithFeatureFlagComponent(props) {
    const { isEnabled, loading } = useFeatureFlags();
    
    if (loading) {
      return null; // Or a loading spinner
    }
    
    if (!isEnabled(flagId)) {
      return FallbackComponent ? <FallbackComponent {...props} /> : null;
    }
    
    return <WrappedComponent {...props} />;
  };
};

// Component to conditionally render children based on feature flag
export const FeatureGate = ({ flagId, children, fallback = null, showUpgradePrompt = false }) => {
  const { isEnabled, requiresUpgrade, getTierRequired, loading } = useFeatureFlags();
  
  if (loading) {
    return null;
  }
  
  if (!isEnabled(flagId)) {
    if (showUpgradePrompt && requiresUpgrade(flagId)) {
      return (
        <div className="p-4 bg-muted/50 rounded-lg border border-border text-center">
          <p className="text-sm text-muted-foreground mb-2">
            This feature requires <span className="font-semibold capitalize">{getTierRequired(flagId)}</span> tier
          </p>
          <a href="/billing" className="text-primary hover:underline text-sm">
            Upgrade to unlock
          </a>
        </div>
      );
    }
    return fallback;
  }
  
  return children;
};

export default FeatureFlagsContext;
